// Instructor Card (Staffing Board) — READ-ONLY instructor-side view of the SAME
// canonical assignment rows the Class Cards read. Class assignments are managed
// only from the Class Editor; this card links there instead of editing.
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ExternalLink } from "lucide-react";
import {
  instructorName,
  type AssignmentRow, type ClassRow, type InstructorRow,
} from "@/lib/staffing";

interface Props {
  instructor: InstructorRow;
  /** Live assignments for this instructor, already scoped to current/upcoming. */
  assignments: AssignmentRow[];
  /** Completed / replaced / cancelled rows — history, never hidden. */
  pastAssignments: AssignmentRow[];
  classes: ClassRow[];
  readiness?: string | null;
  onManageOnClass: (row: AssignmentRow) => void;
}

export default function InstructorStaffingCard({
  instructor, assignments, pastAssignments, classes, readiness, onManageOnClass,
}: Props) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base">{instructorName(instructor)}</CardTitle>
          <Badge variant={instructor.status === "active" ? "secondary" : "outline"} className="shrink-0">
            {instructor.status}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground">
          {instructor.email || "No email on file"}
          {readiness ? ` · ${readiness}` : ""}
        </p>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div>
          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Teaching</p>
          {assignments.length === 0 ? (
            <p className="text-xs text-muted-foreground">No current or upcoming assignments.</p>
          ) : (
            <ul className="space-y-2">
              {assignments.map((a) => {
                const cls = classes.find((c) => c.id === a.class_id || c.slug === a.class_slug);
                const when = [a.day_of_week, [a.start_time, a.end_time].filter(Boolean).join("–")]
                  .filter(Boolean).join(" · ")
                  || cls?.schedule
                  || [cls?.preferred_days, cls?.preferred_time].filter(Boolean).join(" · ")
                  || "Schedule TBD";
                const place = a.location || [cls?.location, cls?.room].filter(Boolean).join(" — ") || "Location TBD";
                return (
                  <li key={a.id} className="min-w-0 rounded-md border p-2">
                    <div className="font-medium break-words">
                      {cls?.title ?? a.class_title ?? a.class_slug}
                      {a.session_label ? ` — ${a.session_label}` : ""}
                    </div>
                    <div className="text-xs text-muted-foreground">{when}</div>
                    <div className="text-xs text-muted-foreground">{place}</div>
                    <div className="text-xs text-muted-foreground">
                      {a.status} · {a.start_date ?? "—"} → {a.end_date ?? "open"}
                    </div>
                    <div className="mt-2">
                      <Button size="sm" variant="outline" onClick={() => onManageOnClass(a)}>
                        <ExternalLink className="mr-1 h-3.5 w-3.5" /> Manage on Class
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {pastAssignments.length > 0 && (
          <div>
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Past assignments
            </p>
            <ul className="space-y-0.5 text-xs text-muted-foreground">
              {pastAssignments.map((a) => {
                const cls = classes.find((c) => c.id === a.class_id || c.slug === a.class_slug);
                return (
                  <li key={a.id}>
                    {cls?.title ?? a.class_title ?? a.class_slug}
                    {a.session_label ? ` — ${a.session_label}` : ""} · {a.status} ·{" "}
                    {a.start_date ?? "—"} → {a.end_date ?? "—"}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <p className="text-[11px] text-muted-foreground">
          Class assignments are managed on the class card. This view is read-only.
        </p>
      </CardContent>
    </Card>
  );
}
